/* hs-eslint ignored failing-rules */
/* eslint-disable prefer-promise-reject-errors */

'use es6';

import { atom as createAtom, deref, unwatch, watch } from 'atom';
import { Map as ImmutableMap } from 'immutable';
import { isResolved, mapResolve } from 'reference-resolvers/utils';
import { Reference, ReferenceSources } from '../references-records';
import { getReferenceResolver, getReferenceResolverLite } from './external-mapping';
import { withCheckbox } from '../checkbox';
import { Metrics } from '../../../../shared/Metrics';
const createReferenceResolverCache = () => {
  let cache = ImmutableMap();
  return key => {
    if (!cache.has(key)) {
      cache = cache.set(key, createAtom());
    }
    return cache.get(key);
  };
};
const referenceResolverCache = createReferenceResolverCache();

// Promisify reference resolver byId interface
const adapt = referenceResolver => {
  const resolverWithCache = referenceResolver(referenceResolverCache);
  if (!resolverWithCache.byId) {
    throw new Error('Resolver is missing ability to fetch byId.');
  }
  return id => new Promise((resolve, reject) => {
    const atom = resolverWithCache.byId(id);
    const watcher = mapResolve({
      loading: () => {},
      error: value => {
        reject(value.reason);
        unwatch(atom, watcher);
      },
      resolved: value => {
        if (isResolved(value)) {
          resolve(value);
        } else {
          reject();
        }
        unwatch(atom, watcher);
      }
    });
    watch(atom, watcher);
    watcher(deref(atom));
  });
};
const defaultGetFallbackReference = id => Reference({
  id,
  label: `Unknown (${id})`,
  source: ReferenceSources.EXTERNAL
});
const formatReferenceRecord = (referenceRecord, id, showId) => Reference({
  id,
  label: showId ? `${referenceRecord.label} (${id})` : referenceRecord.label,
  source: ReferenceSources.EXTERNAL,
  description: referenceRecord.description
});
const FORCE_SHOW_ID_PROPERTIES = ['hs_email_campaign_id', 'hs_email_meta_content_id'];
export const shouldShowId = snowflakeProperty => {
  const {
    name,
    type
  } = snowflakeProperty;
  return FORCE_SHOW_ID_PROPERTIES.includes(name) || type === 'number';
};

// Following dependency injection pattern described here:
//  https://product.hubteam.com/docs/frontend/kb/concatenated-spies.html#eliminate-dependence-on-mutable-imports
//  this allows us to mock a reference resolver for testing
export const getExternalResolverInternal = (snowflakeTable, snowflakeProperty, getResolver, getResolverLite) => {
  const referenceResolver = getResolver(snowflakeTable, snowflakeProperty);
  const referenceResolverLite = getResolverLite(snowflakeTable, snowflakeProperty);
  if (typeof referenceResolver !== 'function' && !referenceResolverLite) {
    return undefined;
  }
  const showId = shouldShowId(snowflakeProperty);
  let singleResolver;
  if (referenceResolverLite) {
    singleResolver = (id, getFallbackReference) => referenceResolverLite.api.byId(id).then(referenceRecord => formatReferenceRecord(referenceRecord, id, showId), error => {
      Metrics.counter('no_resolver', {
        type: 'reference_resolver_lite'
      }).increment();
      return getFallbackReference(id, error);
    });
  } else {
    const adaptedReferenceResolver = adapt(referenceResolver);
    singleResolver = (id, getFallbackReference) => adaptedReferenceResolver(id).then(referenceRecord => formatReferenceRecord(referenceRecord, id, showId)).catch(error => {
      Metrics.counter('no_resolver', {
        type: 'reference_resolver'
      }).increment();
      return getFallbackReference(id, error);
    });
  }
  const baseResolver = (ids, getFallbackReference) => {
    const promises = ids.map(id => singleResolver(id, getFallbackReference).then(reference => [id, reference]));
    return Promise.all(promises).then(references => ImmutableMap(references));
  };
  return (ids, getFallbackReference = defaultGetFallbackReference) => {
    const resolver = withCheckbox(snowflakeProperty)(baseResolver);
    return resolver(ids, getFallbackReference);
  };
};
export const getExternalResolver = (snowflakeTable, snowflakeProperty) => {
  return getExternalResolverInternal(snowflakeTable, snowflakeProperty, getReferenceResolver, getReferenceResolverLite);
};